/**
 * Admin Trade Detail Page
 *
 * Shows comprehensive detail for a single trade including:
 * - Header with status and dates
 * - Parties (initiator & recipient)
 * - Current items (NFTs on each side)
 * - XRP sweetener and escrow details
 * - Signing timeline
 * - Cancel details (if cancelled)
 * - Negotiation history (version diffs)
 * - Wallet context (trade counts, previous trades)
 * - Activity messages
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAdminTradeDetail } from '../services/adminApi.js';
import type {
  AdminTradeDetailResponse,
  AdminTradeDetailItem,
  AdminTradeVersion,
  AdminTradeActivityMessage,
  AdminPreviousTrade,
} from '../types/admin.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { TradeStatusBadge } from '../components/admin/TradeStatusBadge.js';

// ==================== Helpers ====================

function formatXrp(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} XRP`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 8)}...${address.slice(-4)}`;
}

// ==================== Section Components ====================

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function NFTCard({ item }: { item: AdminTradeDetailItem }) {
  const floorXrp = item.floor_price_drops ? (Number(item.floor_price_drops) / 1_000_000).toFixed(2) : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start gap-3">
        {item.nft_thumbnail_url || item.nft_image_url ? (
          <img
            src={item.nft_thumbnail_url || item.nft_image_url || ''}
            alt={item.nft_name || 'NFT'}
            className="h-14 w-14 rounded-md object-cover flex-shrink-0"
          />
        ) : (
          <div className="h-14 w-14 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-xs text-gray-400">No img</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">
            {item.nft_name || 'Unnamed NFT'}
          </div>
          {item.collection_name && (
            <div className="text-xs text-gray-500 truncate">{item.collection_name}</div>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            {item.rarity_rank && (
              <span className="rounded bg-purple-50 px-1.5 py-0.5 text-purple-700">
                Rank #{item.rarity_rank}
              </span>
            )}
            {floorXrp && (
              <span>Floor: {floorXrp} XRP</span>
            )}
          </div>
          <div className="mt-1 text-xs font-mono text-gray-400 truncate">
            {item.nft_token_id.slice(0, 16)}...
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className={`inline-flex rounded-full px-1.5 py-0.5 text-xs font-medium ${
              item.status === 'transferred' ? 'bg-green-100 text-green-700' :
              item.status === 'offer_created' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {item.status}
            </span>
            {item.sell_offer_tx_hash && (
              <a
                href={`https://bithomp.com/en/tx/${encodeURIComponent(item.sell_offer_tx_hash)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-blue-600 hover:text-blue-800 font-mono"
                title={item.sell_offer_tx_hash}
              >
                tx: {item.sell_offer_tx_hash.slice(0, 8)}...
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Main Component ====================

export function AdminTradeDetailPage() {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminTradeDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!tradeId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAdminTradeDetail(tradeId);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load trade detail');
    } finally {
      setLoading(false);
    }
  }, [tradeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading trade detail...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-5xl">
          <button
            onClick={() => navigate('/admin/trades')}
            className="mb-4 text-sm text-blue-600 hover:text-blue-800"
          >
            &larr; Back to trades
          </button>
          <ErrorDisplay error={error} onRetry={loadData} />
        </div>
      </AdminLayout>
    );
  }

  if (!data) return null;

  const { trade, items, versions, activityMessages, walletContext } = data;
  const initiatorItems = items.filter((i) => i.side === 'initiator');
  const recipientItems = items.filter((i) => i.side === 'recipient');

  const hasXrpSweetener = Number(trade.initiator_xrp_drops) > 0 || Number(trade.recipient_xrp_drops) > 0;
  const hasEscrow = trade.initiator_escrow_sequence || trade.recipient_escrow_sequence;
  const isCancelled = ['cancelled', 'declined', 'expired'].includes(trade.status);
  const hasSigningData = trade.initiator_signing_started_at || trade.recipient_signing_started_at;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        {/* Back link */}
        <button
          onClick={() => navigate('/admin/trades')}
          className="mb-4 text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Back to trades
        </button>

        {/* ==================== 1. Header ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Trade</h1>
                <TradeStatusBadge status={trade.status} size="md" />
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <span className="font-mono">{trade.id}</span>
                <button
                  onClick={() => handleCopy(trade.id, 'trade-id')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Copy trade ID"
                >
                  {copiedField === 'trade-id' ? (
                    <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={loadData}
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <InfoRow label="Created" value={formatDate(trade.created_at)} />
            <InfoRow label="Expires" value={formatDate(trade.expires_at)} />
            <InfoRow
              label={trade.status === 'completed' ? 'Completed' : trade.status === 'cancelled' ? 'Cancelled' : 'Updated'}
              value={formatDate(trade.completed_at || trade.cancelled_at || trade.updated_at)}
            />
          </div>
        </div>

        {/* ==================== 2. Parties ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title="Parties" />
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="text-xs font-medium uppercase text-gray-500 mb-2">Initiator</div>
              <div className="text-sm font-medium text-gray-900">
                {walletContext.initiator.username || 'No username'}
              </div>
              <div className="text-xs font-mono text-gray-500 mt-1">
                {trade.initiator_address}
              </div>
              {walletContext.initiator.user_id && (
                <Link
                  to={`/admin/users/${walletContext.initiator.user_id}`}
                  className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-800"
                >
                  View user profile &rarr;
                </Link>
              )}
              <div className="mt-2 text-xs text-gray-500">
                {walletContext.initiator.total_trade_count} total trades
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="text-xs font-medium uppercase text-gray-500 mb-2">Recipient</div>
              <div className="text-sm font-medium text-gray-900">
                {walletContext.recipient.username || 'No username'}
              </div>
              <div className="text-xs font-mono text-gray-500 mt-1">
                {trade.recipient_address}
              </div>
              {walletContext.recipient.user_id && (
                <Link
                  to={`/admin/users/${walletContext.recipient.user_id}`}
                  className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-800"
                >
                  View user profile &rarr;
                </Link>
              )}
              <div className="mt-2 text-xs text-gray-500">
                {walletContext.recipient.total_trade_count} total trades
              </div>
            </div>
          </div>
        </div>

        {/* ==================== 3. Current Items ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title={`Current Items (v${trade.current_version})`} />
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Initiator gives ({initiatorItems.length} NFT{initiatorItems.length !== 1 ? 's' : ''})
              </h3>
              <div className="space-y-2">
                {initiatorItems.length > 0 ? (
                  initiatorItems.map((item) => <NFTCard key={item.id} item={item} />)
                ) : (
                  <p className="text-sm text-gray-400 italic">No NFTs</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Recipient gives ({recipientItems.length} NFT{recipientItems.length !== 1 ? 's' : ''})
              </h3>
              <div className="space-y-2">
                {recipientItems.length > 0 ? (
                  recipientItems.map((item) => <NFTCard key={item.id} item={item} />)
                ) : (
                  <p className="text-sm text-gray-400 italic">No NFTs</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ==================== 4. XRP Sweetener ==================== */}
        {hasXrpSweetener && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="XRP Sweetener" />
            <div className="space-y-1">
              {Number(trade.initiator_xrp_drops) > 0 && (
                <InfoRow label="Initiator pays" value={formatXrp(trade.initiator_xrp_drops)} />
              )}
              {Number(trade.recipient_xrp_drops) > 0 && (
                <InfoRow label="Recipient pays" value={formatXrp(trade.recipient_xrp_drops)} />
              )}
              <InfoRow label="Fee" value={formatXrp(trade.fee_drops)} />

              {hasEscrow && (
                <>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Escrow Details</h3>
                  </div>
                  {trade.initiator_escrow_sequence && (
                    <>
                      <InfoRow label="Initiator Escrow Seq" value={trade.initiator_escrow_sequence} mono />
                      {trade.initiator_escrow_condition && (
                        <InfoRow label="Initiator Condition" value={
                          <span className="max-w-xs truncate inline-block">{trade.initiator_escrow_condition}</span>
                        } mono />
                      )}
                    </>
                  )}
                  {trade.recipient_escrow_sequence && (
                    <>
                      <InfoRow label="Recipient Escrow Seq" value={trade.recipient_escrow_sequence} mono />
                      {trade.recipient_escrow_condition && (
                        <InfoRow label="Recipient Condition" value={
                          <span className="max-w-xs truncate inline-block">{trade.recipient_escrow_condition}</span>
                        } mono />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ==================== 5. Signing Timeline ==================== */}
        {hasSigningData && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="Signing Timeline" />
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="text-xs font-medium uppercase text-gray-500 mb-2">Initiator Signing</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Started</span>
                      <span className={trade.initiator_signing_started_at ? 'text-gray-900' : 'text-gray-400'}>
                        {formatDate(trade.initiator_signing_started_at) || 'Not started'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Completed</span>
                      <span className={trade.initiator_signing_completed_at ? 'text-green-600' : 'text-gray-400'}>
                        {formatDate(trade.initiator_signing_completed_at) || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="text-xs font-medium uppercase text-gray-500 mb-2">Recipient Signing</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Started</span>
                      <span className={trade.recipient_signing_started_at ? 'text-gray-900' : 'text-gray-400'}>
                        {formatDate(trade.recipient_signing_started_at) || 'Not started'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Completed</span>
                      <span className={trade.recipient_signing_completed_at ? 'text-green-600' : 'text-gray-400'}>
                        {formatDate(trade.recipient_signing_completed_at) || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {trade.signing_failure_count > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm">
                  <span className="font-medium text-amber-800">Signing failures:</span>{' '}
                  <span className="text-amber-700">{trade.signing_failure_count} / 3 max</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== 6. Cancel Details ==================== */}
        {isCancelled && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
            <SectionHeader title="Cancel Details" />
            <div className="space-y-2">
              <InfoRow label="Status" value={<TradeStatusBadge status={trade.status} />} />
              <InfoRow label="Reason" value={trade.cancel_reason || 'No reason provided'} />
              <InfoRow label="Cancelled at" value={formatDate(trade.cancelled_at)} />

              {trade.cancel_details && Object.keys(trade.cancel_details).length > 0 && (
                <div className="mt-3 pt-3 border-t border-red-200">
                  <h3 className="text-sm font-medium text-red-800 mb-2">Diagnostic Details</h3>
                  <pre className="rounded-md bg-white border border-red-200 p-3 text-xs text-gray-700 overflow-x-auto max-h-64">
                    {JSON.stringify(trade.cancel_details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== 7. Negotiation History ==================== */}
        {versions.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="Negotiation History" />
            <div className="space-y-4">
              {versions.map((version, idx) => {
                const prevVersion = idx > 0 ? versions[idx - 1] : null;
                const initiatorNftsAdded = prevVersion
                  ? version.initiator_nft_token_ids.filter((id: string) => !prevVersion.initiator_nft_token_ids.includes(id))
                  : [];
                const initiatorNftsRemoved = prevVersion
                  ? prevVersion.initiator_nft_token_ids.filter((id: string) => !version.initiator_nft_token_ids.includes(id))
                  : [];
                const recipientNftsAdded = prevVersion
                  ? version.recipient_nft_token_ids.filter((id: string) => !prevVersion.recipient_nft_token_ids.includes(id))
                  : [];
                const recipientNftsRemoved = prevVersion
                  ? prevVersion.recipient_nft_token_ids.filter((id: string) => !version.recipient_nft_token_ids.includes(id))
                  : [];
                const xrpChanged = prevVersion && (
                  version.initiator_xrp_drops !== prevVersion.initiator_xrp_drops ||
                  version.recipient_xrp_drops !== prevVersion.recipient_xrp_drops
                );

                return (
                  <div key={version.id} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                          v{version.version}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {version.proposed_by_username || truncateAddress(version.proposed_by)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(version.created_at)}</span>
                    </div>

                    {version.message && (
                      <div className="mb-2 text-sm text-gray-600 italic">
                        &ldquo;{version.message}&rdquo;
                      </div>
                    )}

                    <div className="text-xs text-gray-500 space-y-0.5">
                      <div>
                        Initiator NFTs: {version.initiator_nft_token_ids.length} |
                        Recipient NFTs: {version.recipient_nft_token_ids.length}
                      </div>
                      {Number(version.initiator_xrp_drops) > 0 && (
                        <div>Initiator XRP: {formatXrp(version.initiator_xrp_drops)}</div>
                      )}
                      {Number(version.recipient_xrp_drops) > 0 && (
                        <div>Recipient XRP: {formatXrp(version.recipient_xrp_drops)}</div>
                      )}
                    </div>

                    {/* Diffs */}
                    {prevVersion && (
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs space-y-0.5">
                        {initiatorNftsAdded.length > 0 && (
                          <div className="text-green-600">+ {initiatorNftsAdded.length} initiator NFT(s) added</div>
                        )}
                        {initiatorNftsRemoved.length > 0 && (
                          <div className="text-red-600">- {initiatorNftsRemoved.length} initiator NFT(s) removed</div>
                        )}
                        {recipientNftsAdded.length > 0 && (
                          <div className="text-green-600">+ {recipientNftsAdded.length} recipient NFT(s) added</div>
                        )}
                        {recipientNftsRemoved.length > 0 && (
                          <div className="text-red-600">- {recipientNftsRemoved.length} recipient NFT(s) removed</div>
                        )}
                        {xrpChanged && (
                          <div className="text-blue-600">
                            XRP changed: {formatXrp(prevVersion.initiator_xrp_drops)}/{formatXrp(prevVersion.recipient_xrp_drops)}
                            {' '}&rarr;{' '}
                            {formatXrp(version.initiator_xrp_drops)}/{formatXrp(version.recipient_xrp_drops)}
                          </div>
                        )}
                        {initiatorNftsAdded.length === 0 && initiatorNftsRemoved.length === 0 &&
                         recipientNftsAdded.length === 0 && recipientNftsRemoved.length === 0 && !xrpChanged && (
                          <div className="text-gray-400">No changes from previous version</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ==================== 8. Wallet Context ==================== */}
        {walletContext.previousTrades.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="Previous Trades Between Parties" />
            <p className="text-sm text-gray-500 mb-3">
              {walletContext.previousTrades.length} previous trade{walletContext.previousTrades.length !== 1 ? 's' : ''} found
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Trade ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Direction</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {walletContext.previousTrades.map((pt: AdminPreviousTrade) => (
                    <tr
                      key={pt.id}
                      onClick={() => navigate(`/admin/trades/${pt.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-2 font-mono text-gray-600">
                        {pt.id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {pt.initiator_address === trade.initiator_address ? 'Same direction' : 'Reversed'}
                      </td>
                      <td className="px-4 py-2">
                        <TradeStatusBadge status={pt.status} />
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {formatDate(pt.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ==================== 9. Activity Messages ==================== */}
        {activityMessages.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="Activity Messages" />
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activityMessages.map((msg: AdminTradeActivityMessage) => (
                <div key={msg.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {msg.sender_username || truncateAddress(msg.sender_address)}
                      </span>
                      {msg.message_type === 'trade_activity' && msg.trade_action && (
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          {msg.trade_action}
                        </span>
                      )}
                      {msg.trade_version && (
                        <span className="text-xs text-gray-400">v{msg.trade_version}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500" title={formatDate(msg.created_at)}>
                      {formatRelative(msg.created_at)}
                    </span>
                  </div>
                  {msg.content && (
                    <p className="text-sm text-gray-600">{msg.content}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
